import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function AdminConfigContratante() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [fallbackEnv, setFallbackEnv] = useState(false);
  const [form, setForm] = useState({
    razao_social: '',
    cnpj: '',
    endereco: '',
  });

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const data = await apiAdmin.get('/api/admin/config/contratante');
      setForm({
        razao_social: data.razao_social || '',
        cnpj: data.cnpj || '',
        endereco: data.endereco || '',
      });
      setFallbackEnv(!!data.fallback_env_ativo);
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(err?.message || 'Erro ao carregar');
      toast.error(getDisplayMessage(err, 'Erro ao carregar cadastro da CONTRATADA (prestadora).'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErro('');
    try {
      const data = await apiAdmin.put('/api/admin/config/contratante', {
        razao_social: form.razao_social.trim(),
        cnpj: form.cnpj.replace(/\D/g, ''),
        endereco: form.endereco.trim(),
      });
      setForm({
        razao_social: data.razao_social || '',
        cnpj: data.cnpj || '',
        endereco: data.endereco || '',
      });
      setFallbackEnv(!!data.fallback_env_ativo);
      toast.success('Dados da CONTRATADA (prestadora) salvos. Eles serão usados na geração de contratos por IA.');
    } catch (err) {
      setErro(err?.message || 'Erro ao salvar');
      toast.error(getDisplayMessage(err, 'Erro ao salvar.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Config. CONTRATADA</h1>
        <p className="text-slate-600">
          Cadastro da prestadora do SaaS (qualificada como <span className="font-medium">CONTRATADA</span> nos contratos
          gerados por IA em Empresas &gt; Contrato). Se não houver razão social e CNPJ (14 dígitos) aqui nem em{' '}
          <code className="text-xs bg-slate-100 px-1 rounded">CONTRATO_CONTRATANTE_*</code>, usa-se a qualificação
          padrão Ever Tecnologia no texto do contrato. A rota da API mantém o nome legado{' '}
          <code className="text-xs bg-slate-100 px-1 rounded">/config/contratante</code>.
        </p>
      </div>

      {fallbackEnv && (
        <Alert>
          <AlertDescription>
            Não há cadastro no banco. Enquanto isso, a geração pode usar variáveis de ambiente{' '}
            <code className="text-xs bg-slate-100 px-1 rounded">CONTRATO_CONTRATANTE_*</code>. Salve aqui para fixar os
            dados no cadastro.
          </AlertDescription>
        </Alert>
      )}

      {erro && (
        <Alert variant="destructive">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Dados jurídicos</CardTitle>
          <CardDescription>
            Com razão social e CNPJ (14 dígitos), a IA monta a qualificação no formato: &quot;...doravante denominada
            simplesmente CONTRATADA&quot;.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="razao_social">Razão social</Label>
              <Input
                id="razao_social"
                value={form.razao_social}
                onChange={(e) => setForm((f) => ({ ...f, razao_social: e.target.value }))}
                placeholder="Nome da empresa prestadora"
                autoComplete="organization"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ (somente números, 14 dígitos)</Label>
              <Input
                id="cnpj"
                inputMode="numeric"
                value={form.cnpj}
                onChange={(e) => setForm((f) => ({ ...f, cnpj: e.target.value.replace(/\D/g, '').slice(0, 14) }))}
                placeholder="00000000000000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endereco">Endereço da sede</Label>
              <Textarea
                id="endereco"
                rows={3}
                value={form.endereco}
                onChange={(e) => setForm((f) => ({ ...f, endereco: e.target.value }))}
                placeholder="Logradouro, número, bairro, cidade/UF, CEP"
              />
            </div>
            <Button type="submit" disabled={saving} className="bg-slate-800 hover:bg-slate-700">
              {saving ? 'Salvando...' : 'Salvar cadastro'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
